import type { Service } from '@/types/services';
import { getCategoryLabel } from '@/components/services/ServiceCategories';

/**
 * Search titles and descriptions for /services/:id.
 *
 * ServiceDetail renders these at runtime and scripts/prerender.mjs writes them
 * into the static HTML, both from this one map. Titles are kept to 60
 * characters or fewer and aimed at the US searches each page can rank for.
 *
 * Services with a `customLink` have their own landing page. Their
 * /services/:id URL is 301-redirected there (see prerender.mjs), so they do
 * not need an entry.
 */
const SEO: Record<string, { title: string; description: string }> = {
  'ai-chatbots': {
    title: 'AI Chatbot Development for US Businesses | Omnicraft',
    description: 'Custom AI chatbots and assistants for customer support, lead qualification and internal knowledge, built on OpenAI and Claude and connected to your tools.',
  },
  'web-software-development': {
    title: 'Custom Web Design & Software Development | Omnicraft',
    description: 'Hand-coded websites, web apps and mobile apps for US businesses. No page builders or WordPress bloat: fast, secure and built to rank on Google.',
  },
  'seo-analytics': {
    title: 'SEO & Analytics Services for Small Businesses | Omnicraft',
    description: 'Technical SEO, content and analytics set-up for US small businesses: fix indexing, track real leads in GA4 and rank for the searches your customers make.',
  },
  'creative-content': {
    title: 'Branding, Content & AI Video Production | Omnicraft',
    description: 'Logo and brand design, website copy, social content and AI-generated video for small businesses that need a consistent, professional look.',
  },
  'rag-knowledge-assistant': {
    title: 'RAG Knowledge Assistant Development | Omnicraft',
    description: 'An AI assistant that answers from your own documents and database, with the source attached to every answer. Built, secured and deployed for your team.',
  },
  'ai-agents-mcp': {
    title: 'AI Agents & MCP Server Development | Omnicraft',
    description: 'Custom AI agents and MCP servers that connect Claude or ChatGPT to your own systems, plus Claude Code set up and configured for your development team.',
  },
  'document-data-extraction': {
    title: 'Invoice & Document Data Extraction with AI | Omnicraft',
    description: 'AI and OCR that read invoices, commercial invoices, delivery notes and forms and turn them into clean, structured data for your accounting or ERP system.',
  },
  'ai-app-rescue': {
    title: 'Fix Your Lovable, Bolt or Replit App | Omnicraft',
    description: 'Your AI-built app works in the builder and breaks with real users. We fix the auth, database and security underneath and deploy it to production.',
  },
  'supabase-backend': {
    title: 'Supabase Development Services | Omnicraft',
    description: 'Supabase backend development: Postgres schema, auth, row level security, edge functions and storage, built properly, tested and documented.',
  },
  'saas-mvp': {
    title: 'SaaS MVP Development for Startups | Omnicraft',
    description: 'A working SaaS MVP that real users can log into: auth, roles, your core feature, admin views and billing, deployed on a stack you own.',
  },
  'architectural-services': {
    title: 'Remote Architectural Drafting & 3D Visualization | Omnicraft',
    description: 'Remote architectural drafting, floor plans, layouts and 3D visualization for residential and commercial projects, delivered to your deadlines.',
  },
};

export const serviceSeo = (service: Service) =>
  SEO[service.id] ?? {
    title: `${service.title} | Omnicraft Services`,
    description: `${service.shortDescription} ${getCategoryLabel(service.category)} services from Omnicraft for US businesses.`.slice(0, 160),
  };
